<template>
  <v-dialog :model-value="modelValue" max-width="640" scrollable @update:model-value="$emit('update:modelValue', $event)">
    <div v-if="profile" class="pdlg" role="dialog" aria-modal="true" aria-labelledby="add-npi-h">
      <div class="pdlg-head">
        <h2 id="add-npi-h">Add NPI for Dr. {{ profile.name }}</h2>
        <button type="button" class="pdlg-x" aria-label="Close" @click="close">&#10005;</button>
      </div>
      <div class="pdlg-body">
        <NpiSearchField
          :key="searchKey"
          :initial-query="profile.name"
          :review-entity-id="reviewEntityId"
          label="Find this provider in the NPI Registry"
          @pick="onPick"
        >
          <template #help>We searched using the name you added. You can also type the 10-digit NPI.</template>
        </NpiSearchField>
        <span class="pdlg-hint">Not listed? The provider may not be in the U.S. registry. You can leave the NPI blank.</span>
        <RegistryCard v-if="picked" :result="picked">
          We'll fill in the specialty and practice locations from the registry. If this NPI is already on another provider profile, we'll ask before merging them.
        </RegistryCard>
        <v-alert v-if="error" type="error" variant="tonal" density="compact">{{ error }}</v-alert>
      </div>
      <div class="pdlg-foot">
        <div class="pdlg-btns">
          <button type="button" class="pdlg-btn" @click="close">Cancel</button>
          <button type="button" class="pdlg-btn primary" :disabled="!picked || saving" @click="submit">
            {{ saving ? 'Adding…' : 'Add NPI' }}
          </button>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import NpiSearchField from './NpiSearchField.vue'
import RegistryCard from './RegistryCard.vue'
import { assignNpi } from './providersApi'
import { track } from './track'
import { errorMessage } from './providerErrors'

export default {
  name: 'AddNpiDialog',
  components: { NpiSearchField, RegistryCard },
  props: {
    modelValue: { type: Boolean, default: false },
    reviewEntityId: { type: [Number, String], default: null },
    profile: { type: Object, default: null }
  },
  emits: ['update:modelValue', 'assigned', 'taken'],
  data() {
    return { picked: null, saving: false, error: '', searchKey: 0 }
  },
  watch: {
    modelValue(open) {
      if (open) Object.assign(this, { picked: null, saving: false, error: '', searchKey: this.searchKey + 1 })
    }
  },
  methods: {
    close() {
      this.$emit('update:modelValue', false)
    },
    onPick(r) {
      this.picked = r
      this.error = ''
    },
    async submit() {
      if (!this.picked || this.saving) return
      this.saving = true
      this.error = ''
      try {
        const res = await assignNpi(this.reviewEntityId, this.profile.id, this.picked.npi)
        track('provider_npi_added')
        this.$emit('assigned', res.provider)
        this.close()
      } catch (e) {
        const data = e.response?.data
        if (e.response?.status === 409 && data?.code === 'npi_taken') {
          this.$emit('taken', { existing: data.existing })
        } else {
          this.error = errorMessage(e, "We couldn't add this NPI. Try again.")
        }
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped src="./dialogStyles.css"></style>
<style scoped>
.pdlg { background: #fff; border-radius: 14px; display: flex; flex-direction: column; max-height: calc(100vh - 48px); overflow-y: auto; box-shadow: 0 16px 48px rgba(13, 30, 53, 0.25); }
.pdlg-hint { font-size: 13px; color: #4a5568; }
</style>
